import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface TechnicalChecks {
  robotsTxt: {
    status: 'allowed' | 'partially_blocked' | 'blocked' | 'not_found' | 'unknown';
    blockedBots: string[];
  };
  llmsTxt: { status: 'found' | 'not_found' | 'unknown' };
  sitemap: { status: 'found' | 'not_found' | 'unknown' };
  schemaMkp: { status: 'found' | 'not_found' | 'unknown'; types: string[] };
}

export type TechCheckStatus = 'idle' | 'loading' | 'done' | 'error';

export interface TechnicalCheckState {
  status: TechCheckStatus;
  data: TechnicalChecks | null;
  passed: number;
  failed: number;
  warnings: number;
  total: number;
}

const CACHE_KEY = 'ailens_tech_checks_cache';
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

function getCheckResult(data: TechnicalChecks) {
  let passed = 0, failed = 0, warnings = 0;

  // robots.txt
  if (data.robotsTxt.status === 'allowed') passed++;
  else if (data.robotsTxt.status === 'partially_blocked') warnings++;
  else failed++;

  // sitemap
  if (data.sitemap.status === 'found') passed++;
  else failed++;

  // llms.txt — warning instead of fail
  if (data.llmsTxt.status === 'found') passed++;
  else warnings++;

  // schema
  if (data.schemaMkp.status === 'found') passed++;
  else failed++;

  return { passed, failed, warnings, total: 4 };
}

function getCached(url: string): TechnicalChecks | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const entry = JSON.parse(raw);
    if (entry.url === url && Date.now() - entry.timestamp < CACHE_TTL_MS) {
      return entry.data;
    }
  } catch { /* ignore */ }
  return null;
}

function setCache(url: string, data: TechnicalChecks) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ url, data, timestamp: Date.now() }));
  } catch { /* ignore */ }
}

export function useTechnicalChecks(websiteUrl?: string): TechnicalCheckState {
  const [status, setStatus] = useState<TechCheckStatus>('idle');
  const [data, setData] = useState<TechnicalChecks | null>(null);

  const run = useCallback(async (url: string) => {
    // Check cache first
    const cached = getCached(url);
    if (cached) {
      setData(cached);
      setStatus('done');
      return;
    }

    setStatus('loading');
    try {
      const { data: result, error } = await supabase.functions.invoke('technical-readiness-check', {
        body: { websiteUrl: url },
      });
      if (error) throw error;
      setData(result);
      setStatus('done');
      setCache(url, result);
    } catch (err) {
      console.error('[TECHNICAL] Auto-check failed:', err);
      setStatus('error');
    }
  }, []);

  // Auto-run when websiteUrl is available
  useEffect(() => {
    if (websiteUrl && status === 'idle') {
      run(websiteUrl);
    }
  }, [websiteUrl, status, run]);

  const counts = data ? getCheckResult(data) : { passed: 0, failed: 0, warnings: 0, total: 4 };

  return { status, data, ...counts };
}
